import moment from 'moment';
import { useEffect, useRef, useState } from 'react';
import { BackButton } from '../../components/BackButton';
import { THUMBNAIL_IMAGE_SIZING } from '../../components/EntityImage';
import { GuestPicker } from '../../components/GuestPicker';
import { CloseIcon } from '../../icons/CloseIcon';
import { DatePicker } from '../DatePicker';
import { RateCodeField } from '../JourneyPicker/RateCodeField';
import {
  CategoryList,
  CategoryOption,
  CategoryThumb,
  CategoryThumbImg,
  CloseButton,
  DateStep,
  Footer,
  HeaderRow,
  NextButton,
  ResetButton,
  Section,
  SectionBody,
  SectionHeader,
  SectionLabel,
  SectionValue,
  Sections,
  Wrapper,
} from './MobileJourneyPicker.styles';
import type {
  MobileJourneyPickerProps,
  MobileJourneyPickerSection,
} from './MobileJourneyPicker.types';

type DraftDates = { checkIn: string | null; checkOut: string | null };

function formatRangeLabel(from?: string | null, to?: string | null): string | null {
  if (!from) return null;
  const fromM = moment(from, 'YYYY-MM-DD');
  if (!to || from === to) return fromM.format('D MMM');
  const toM = moment(to, 'YYYY-MM-DD');
  if (fromM.month() === toM.month() && fromM.year() === toM.year()) {
    return `${fromM.format('D')} - ${toM.format('D MMM')}`;
  }
  return `${fromM.format('D MMM')} - ${toM.format('D MMM')}`;
}

export function MobileJourneyPicker({
  categories,
  category,
  onCategoryChange,
  fromDate,
  toDate,
  availability,
  showPastDates = false,
  onRangeSelect,
  onMonthChange,
  adults,
  childCount,
  onAdultsChange,
  onChildrenChange,
  minAdults,
  maxAdults,
  minChildren,
  maxChildren,
  onClose,
  onReset,
  onNext,
  defaultOpenSection,
  visibleMonthCount = 2,
  showWho = true,
  isSingleDate = false,
  showRate = false,
  rateCode,
  onRateCodeChange,
  labels: l,
}: MobileJourneyPickerProps) {
  const showWhat = !!categories && categories.length > 0;
  const initialOpen = defaultOpenSection ?? (showWhat ? 'what' : 'when');
  const [open, setOpen] = useState<MobileJourneyPickerSection | null>(initialOpen);

  const [draftDates, setDraftDates] = useState<DraftDates>({
    checkIn: fromDate ?? null,
    checkOut: toDate ?? null,
  });
  const dateStepRef = useRef<HTMLDivElement>(null);
  const whenHeaderRef = useRef<HTMLButtonElement>(null);
  const whoHeaderRef = useRef<HTMLButtonElement>(null);
  const pendingFocus = useRef<'dates' | 'when' | 'who' | null>(
    initialOpen === 'when' ? 'dates' : null
  );

  useEffect(() => {
    const target = pendingFocus.current;
    pendingFocus.current = null;
    if (target === 'dates') dateStepRef.current?.focus();
    if (target === 'when') whenHeaderRef.current?.focus();
    if (target === 'who') whoHeaderRef.current?.focus();
  }, [open]);

  const toggle = (section: MobileJourneyPickerSection) => {
    setOpen((current) => (current === section ? null : section));
  };

  const openDates = () => {
    pendingFocus.current = 'dates';
    setDraftDates({ checkIn: fromDate ?? null, checkOut: toDate ?? null });
    setOpen('when');
  };

  const leaveDates = (next: 'who' | null) => {
    pendingFocus.current = next ?? 'when';
    setOpen(next);
  };

  const updateDraftDates = (checkIn: string | null, checkOut: string | null) =>
    setDraftDates({ checkIn, checkOut });

  const applyDates = () => {
    if (draftDates.checkIn) {
      onRangeSelect?.(draftDates.checkIn, draftDates.checkOut ?? draftDates.checkIn);
    }
    leaveDates(showWho ? 'who' : null);
  };

  const currentCategory = categories?.find((c) => c.id === category);
  const dateRangeLabel = formatRangeLabel(fromDate, toDate);
  const guestsTotal = adults + childCount;

  const hasDates = !!fromDate;
  const hasGuests = guestsTotal > 0;
  const hasAnyValue = !!currentCategory || hasDates || hasGuests;
  const canApplyDates = !!draftDates.checkIn && (isSingleDate || !!draftDates.checkOut);

  const closeButton = onClose && (
    <CloseButton type="button" onClick={onClose} aria-label={l?.closeAriaLabel ?? 'Close'}>
      <CloseIcon size={16} />
    </CloseButton>
  );

  const isOnDates = open === 'when';

  return (
    <>
      {isOnDates && (
        <Wrapper>
          <HeaderRow data-step="dates">
            <BackButton
              variant="inline"
              onClick={() => leaveDates(null)}
              ariaLabel={l?.back ?? 'Back'}
            />
            {closeButton}
          </HeaderRow>
          <DateStep ref={dateStepRef} role="region" aria-label={l?.when ?? 'When?'} tabIndex={-1}>
            <DatePicker
              checkIn={draftDates.checkIn}
              checkOut={draftDates.checkOut}
              singleDateMode={isSingleDate}
              numberOfMonths={visibleMonthCount === 1 ? 1 : 2}
              availability={availability}
              showPastDates={showPastDates}
              onRangeSelect={updateDraftDates}
              onMonthChange={onMonthChange}
              onSelectionChange={updateDraftDates}
              stacked
              pageScroll
              labels={l?.calendar}
            />
          </DateStep>
          <Footer data-align="end">
            <NextButton type="button" onClick={applyDates} disabled={!canApplyDates}>
              {l?.calendar?.apply ?? 'Apply'}
            </NextButton>
          </Footer>
        </Wrapper>
      )}
      <Wrapper hidden={isOnDates}>
        <HeaderRow>{closeButton}</HeaderRow>

        <Sections>
          {showWhat && (
            <Section>
              <SectionHeader
                type="button"
                onClick={() => toggle('what')}
                aria-expanded={open === 'what'}
              >
                <SectionLabel>{l?.what ?? 'What?'}</SectionLabel>
                <SectionValue $placeholder={!currentCategory}>
                  {currentCategory?.label ?? l?.addCategory ?? 'Add category'}
                </SectionValue>
              </SectionHeader>
              {open === 'what' && (
                <SectionBody>
                  <CategoryList>
                    {categories!.map((cat) => (
                      <CategoryOption
                        key={cat.id}
                        type="button"
                        $active={cat.id === category}
                        onClick={() => {
                          onCategoryChange?.(cat.id);
                          openDates();
                        }}
                      >
                        <span>{cat.label}</span>
                        <CategoryThumb>
                          {cat.imageUrl && (
                            <CategoryThumbImg
                              src={cat.imageUrl}
                              alt={cat.imageAlt ?? ''}
                              sizing={THUMBNAIL_IMAGE_SIZING}
                            />
                          )}
                        </CategoryThumb>
                      </CategoryOption>
                    ))}
                  </CategoryList>
                </SectionBody>
              )}
            </Section>
          )}

          <Section>
            <SectionHeader
              ref={whenHeaderRef}
              type="button"
              onClick={openDates}
              aria-expanded={false}
            >
              <SectionLabel>{l?.when ?? 'When?'}</SectionLabel>
              <SectionValue $placeholder={!dateRangeLabel}>
                {dateRangeLabel ?? l?.addDates ?? 'Add dates'}
              </SectionValue>
            </SectionHeader>
          </Section>

          {showWho && (
            <Section>
              <SectionHeader
                ref={whoHeaderRef}
                type="button"
                onClick={() => toggle('who')}
                aria-expanded={open === 'who'}
              >
                <SectionLabel>{l?.who ?? 'Who?'}</SectionLabel>
                <SectionValue $placeholder={!hasGuests}>
                  {hasGuests
                    ? `${guestsTotal} ${guestsTotal === 1 ? 'Guest' : 'Guests'}`
                    : l?.addGuests ?? 'Add guests'}
                </SectionValue>
              </SectionHeader>
              {open === 'who' && (
                <SectionBody>
                  <GuestPicker
                    adults={adults}
                    childCount={childCount}
                    onAdultsChange={onAdultsChange}
                    onChildrenChange={onChildrenChange}
                    minAdults={minAdults}
                    maxAdults={maxAdults}
                    minChildren={minChildren}
                    maxChildren={maxChildren}
                    renderInline
                    labels={l?.guestPicker}
                  />
                </SectionBody>
              )}
            </Section>
          )}
          {showRate && onRateCodeChange && (
            <Section>
              <SectionBody $noPadding>
                <RateCodeField
                  value={rateCode}
                  placeholder={l?.rateCode ?? 'Rate code'}
                  applyLabel={l?.rateApply ?? 'Apply'}
                  clearLabel={l?.rateClear ?? 'Clear rate code'}
                  onApply={(code) => onRateCodeChange(code)}
                  onClear={() => onRateCodeChange('')}
                />
              </SectionBody>
            </Section>
          )}
        </Sections>

        <Footer>
          <ResetButton type="button" onClick={onReset} disabled={!onReset || !hasAnyValue}>
            {l?.reset ?? 'Reset'}
          </ResetButton>
          <NextButton type="button" onClick={onNext} disabled={!onNext}>
            {l?.next ?? 'Next'}
          </NextButton>
        </Footer>
      </Wrapper>
    </>
  );
}
